/**
 * Yjs collab client (T-0032 / E04-F01 §4).
 *
 * Wraps `y-websocket`'s WebsocketProvider so callers get a typed Y.Doc + a
 * connection status they can render directly. The relay (apps/yjs-relay) is
 * the WS counterpart; the wire protocol is the standard y-websocket binary
 * shape so the published client is the natural choice.
 *
 * Auth: the bearer token from the auth store is appended as `?token=<token>`
 * to the WS URL. Browsers cannot set Authorization on WS upgrades, so the
 * query parameter is the documented fallback the relay's `extractBearerToken`
 * understands.
 *
 * Status surface:
 *   - 'connecting' — initial connection in flight; UI may render a spinner
 *   - 'connected'  — sync handshake complete + live edits flow
 *   - 'disconnected' — transient drop; the WebsocketProvider auto-reconnects
 *   - 'degraded'  — server-side deny (4401 / 4403 / 4404) OR the auth token
 *                    is missing; the UI surfaces the "live collaboration
 *                    unavailable" banner and falls back to REST PATCH
 *
 * The 'degraded' verdict is sticky: once we hit a server-side deny we stop
 * retrying so the user isn't churning. A page reload (or restoring auth)
 * resets the client.
 */

import * as Y from 'yjs';
import { WebsocketProvider } from 'y-websocket';
import { Awareness } from 'y-protocols/awareness';

export type CollabStatus = 'connecting' | 'connected' | 'disconnected' | 'degraded';

export interface CollabClient {
  doc: Y.Doc;
  awareness: Awareness;
  status: () => CollabStatus;
  /** Subscribe to status changes. Returns an unsubscribe function. */
  onStatus: (listener: (status: CollabStatus) => void) => () => void;
  close: () => void;
}

export interface CreateCollabClientArgs {
  engagementId: string;
  registerType: string;
  token: string | null;
  /**
   * Override the relay URL. Production lets the same-origin Vite/proxy
   * handle the mount; the override is the test seam + the explicit URL the
   * Vite dev proxy uses behind a non-default port.
   */
  relayUrl?: string;
}

const DEFAULT_PATH_PREFIX = '/yjs';

function buildWsBase(relayUrl: string | undefined): string {
  if (relayUrl) {
    // The caller can either pass a full `ws://host:port/prefix` OR just the
    // path prefix `/yjs`. Detect the protocol form.
    if (/^wss?:/.test(relayUrl)) return relayUrl;
    return relativeWsBase(relayUrl);
  }
  return relativeWsBase(DEFAULT_PATH_PREFIX);
}

function relativeWsBase(prefix: string): string {
  if (typeof window === 'undefined') {
    // Defensive — SSR doesn't apply here but the type system asks.
    return `ws://localhost:3001${prefix}`;
  }
  const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
  return `${proto}://${window.location.host}${prefix}`;
}

/**
 * Build a CollabClient bound to (engagement_id, register_type). Returns null
 * when no auth token is present — the caller renders the degraded banner.
 */
export function createCollabClient(args: CreateCollabClientArgs): CollabClient | null {
  if (!args.token) return null;

  const doc = new Y.Doc();
  // Eagerly materialize the entries map so the projection-side shape contract
  // is symmetrical (the relay's Y.Doc also pre-creates this map).
  doc.getMap('entries');

  const base = buildWsBase(args.relayUrl);
  const roomName = `registers/${args.engagementId}/${args.registerType}`;
  const params: Record<string, string> = { token: args.token };

  let status: CollabStatus = 'connecting';
  const listeners = new Set<(s: CollabStatus) => void>();

  const provider = new WebsocketProvider(base, roomName, doc, {
    connect: true,
    params,
  });

  const updateStatus = (next: CollabStatus): void => {
    if (status === next) return;
    status = next;
    listeners.forEach((l) => l(next));
  };

  provider.on('status', (event: { status: string }) => {
    if (status === 'degraded') return;
    if (event.status === 'connected') updateStatus('connected');
    else if (event.status === 'connecting') updateStatus('connecting');
    else if (event.status === 'disconnected') updateStatus('disconnected');
  });

  // The provider exposes the underlying socket. Listen for the WS close code
  // and surface the 4-prefix deny shapes as 'degraded' so the UI shows the
  // banner + the client stops reconnecting against an engagement it cannot
  // join.
  provider.on('connection-close', (event: CloseEvent | null) => {
    if (!event) return;
    if (event.code >= 4400 && event.code < 4500) {
      // Auth / not-found denies — degrade the client and stop reconnecting.
      try {
        provider.shouldConnect = false;
        provider.disconnect();
      } catch {
        // ignore
      }
      updateStatus('degraded');
    }
  });

  // Also surface a degraded verdict when the provider can't establish a
  // connection at all (relay process down). The WebsocketProvider already
  // attempts auto-reconnect; we don't override that, but after the first
  // few failures we surface 'degraded' so the UI doesn't sit in 'connecting'
  // forever.
  let failedConnects = 0;
  provider.on('connection-error', () => {
    failedConnects += 1;
    if (failedConnects >= 3 && status !== 'connected') {
      updateStatus('degraded');
    }
  });

  return {
    doc,
    awareness: provider.awareness,
    status: () => status,
    onStatus: (listener) => {
      listeners.add(listener);
      // Push the current status synchronously so subscribers don't miss the
      // initial 'connecting' tick.
      listener(status);
      return () => listeners.delete(listener);
    },
    close: () => {
      try {
        provider.disconnect();
        provider.destroy();
      } catch {
        // ignore
      }
      doc.destroy();
      listeners.clear();
    },
  };
}
